import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext } from '../../context';
import { ChannelAppTabUpdateInput } from '../../services';
import {
  AppTabsSettings,
  AvatarUpload,
  Button,
  ButtonType,
  IconLabel,
  ImageFile,
  InputField,
  LanguageByCode,
  LOCALES,
  normalizeSubdomain,
  RestrictDuplicateUrlOfCardsInTimeFrame,
  Select,
  useAppDispatch,
  useRestrictDuplicateUrlOfCardsInTimeFrame,
} from '../../shared';
import { channelUpdate } from '../../slices';

import classes from './ChannelSettings.module.scss';

export const ChannelSettings: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'channelSettings' });

  const dispatch = useAppDispatch();

  const { options: restrictDuplicateUrlOfCardsInTimeFrameOptions } =
    useRestrictDuplicateUrlOfCardsInTimeFrame();

  const { channel, setChannel } = useContext(ChannelContext);

  const {
    id: channelId,
    name: channelName,
    subdomain: channelSubdomain,
    image: channelImage,
    localeId: channelLocaleId,
    restrictDuplicateUrlOfCards: channelRestrictDuplicateUrlOfCards,
    appTabsSettings: { appTabs: channelAppTabs },
  } = channel;

  const [image, setImage] = useState<ImageFile | null>(channelImage ?? null);

  const [imageLoading, setImageLoading] = useState<boolean>(false);

  const [name, setName] = useState<string>(channelName);

  const [subdomain, setSubdomain] = useState<string>(channelSubdomain);

  const [lockSubdomain, setLockSubdomain] = useState<boolean>(true);

  const [localeId, setLocaleId] = useState<number>(channelLocaleId);

  const [appTabs, setAppTabs] = useState<ChannelAppTabUpdateInput[]>(
    channelAppTabs.map(({ action, defaultIconUrl, iconUrl, title }) => {
      return {
        action,
        iconUrl: { x1: iconUrl.x1 || defaultIconUrl.x1, x3: iconUrl.x3 || defaultIconUrl.x3 },
        title,
      };
    })
  );

  const [loading, setLoading] = useState<boolean>(false);

  const [restrictDuplicateUrlOfCards, setRestrictDuplicateUrlOfCards] =
    useState<RestrictDuplicateUrlOfCardsInTimeFrame>(channelRestrictDuplicateUrlOfCards);

  const localeOptions = useMemo(
    () =>
      LOCALES.map(({ id, code }) => {
        return { id, title: LanguageByCode[code as keyof typeof LanguageByCode] };
      }),
    []
  );

  const onSave = useCallback(async () => {
    setLoading(true);

    try {
      const { payload } = await toast.promise(
        dispatch(
          channelUpdate({
            channelId,
            fields: {
              name,
              subdomain,
              localeId,
              imageId: image?.id ?? null,
              restrictDuplicateUrlOfCards,
              appTabs,
            },
          })
        ).unwrap(),
        { pending: ctxT('save-pending'), success: ctxT('save-success') }
      );

      if (payload) {
        setChannel({ ...channel, ...payload });
        return;
      }
    } catch (_) {
      toast.error(ctxT('save-error'));
    } finally {
      setLoading(false);
    }
  }, [
    appTabs,
    channel,
    channelId,
    ctxT,
    dispatch,
    image?.id,
    localeId,
    name,
    restrictDuplicateUrlOfCards,
    setChannel,
    subdomain,
  ]);

  return (
    <div className={classes['settings']}>
      <h1 className={classes['settings__title']}>{ctxT('title')}</h1>
      <div className={classes['settings__subtitle']}>{ctxT('subtitle')}</div>

      <AvatarUpload
        avatar={image}
        setAvatar={setImage}
        avatarSize={72}
        loading={imageLoading}
        setLoading={setImageLoading}
        title={ctxT('change-picture')}
        className={classes['settings__avatar-upload']}
      />

      <InputField
        value={name}
        label={ctxT('name')}
        onChange={({ target }) => setName(target.value)}
      />

      <div className={classes['settings__subdomain-wrapper']}>
        <InputField
          value={subdomain}
          label={ctxT('subdomain')}
          onChange={({ target }) => setSubdomain(normalizeSubdomain(target.value))}
          disabled={lockSubdomain}
          icon={
            <IconLabel
              iconId={'lock'}
              iconSize={20}
              ariaLabel={t(`common.${lockSubdomain ? 'unlock' : 'lock'}`)}
              onClick={() => setLockSubdomain(!lockSubdomain)}
              singleColor
            />
          }
        />

        <div className={classes['settings__subdomain-warning']}>{ctxT('subdomain-warning')}</div>
      </div>

      <div className={classes['settings__language-wrapper']}>
        <Select
          selectedItemId={localeId}
          label={ctxT('language')}
          items={localeOptions}
          onChange={(localeId) => setLocaleId(localeId as number)}
        />

        <div className={classes['settings__language-description']}>
          {ctxT('language-description')}
        </div>
      </div>

      <div className={classes['settings__duplicates-wrapper']}>
        <Select
          selectedItemId={restrictDuplicateUrlOfCards}
          label={ctxT('restrict-duplicates')}
          items={restrictDuplicateUrlOfCardsInTimeFrameOptions}
          onChange={(value) =>
            setRestrictDuplicateUrlOfCards(value as RestrictDuplicateUrlOfCardsInTimeFrame)
          }
        />

        <div className={classes['settings__duplicates-description']}>
          {ctxT('duplicates-description')}
        </div>
      </div>

      <AppTabsSettings
        appTabs={appTabs}
        setAppTabs={setAppTabs}
        langHintLocaliseKey={'appTabsSettings.channel-lang-hint'}
        iconHintLocaliseKey={'appTabsSettings.channel-icon-hint'}
      />

      <Button
        type={ButtonType.primary}
        label={t('common.save')}
        disabled={loading || imageLoading}
        loading={loading}
        onClick={onSave}
      />
    </div>
  );
};
